import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SessionTimeout: React.FC = () => {
  const { logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const checkSessionTimeout = () => {
      const storedTimestamp = localStorage.getItem('authTimestamp');
      
      if (storedTimestamp) {
        const timestamp = parseInt(storedTimestamp);
        const now = Date.now();
        const hoursSinceLogin = (now - timestamp) / (1000 * 60 * 60);
        const hoursLeft = 24 - hoursSinceLogin;
        
        // Show warning when less than 1 hour left
        if (hoursLeft <= 1 && hoursLeft > 0) {
          setShowWarning(true);
          setTimeLeft(Math.floor(hoursLeft * 60)); // Convert to minutes
        } else if (hoursLeft <= 0) {
          // Session expired, logout
          logout();
        }
      }
    };

    // Check immediately
    checkSessionTimeout();
    
    // Check every minute
    const interval = setInterval(checkSessionTimeout, 60000);
    
    return () => clearInterval(interval);
  }, [logout]);

  useEffect(() => {
    if (showWarning && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            logout();
            return 0;
          }
          return prev - 1;
        });
      }, 60000); // Update every minute
      
      return () => clearInterval(timer);
    }
  }, [showWarning, timeLeft, logout]);

  if (!showWarning) return null;

  return (
    <div className="fixed top-4 right-4 z-50">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg max-w-sm">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              Session Timeout Warning
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Your session will expire in {timeLeft} minute{timeLeft !== 1 ? 's' : ''}.</p>
              <p className="mt-1">Please save your work and log in again if needed.</p>
            </div>
            <div className="mt-3 flex space-x-2">
              <button
                onClick={() => setShowWarning(false)}
                className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded hover:bg-yellow-200 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={logout}
                className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700 transition-colors"
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout; 